/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { throwError } from 'rxjs';
import { TenantService } from '@src/app/shared/tenant.service';
import { inject } from '@angular/core';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';

  

function urlWhitelisted(path: string) {
    if (path.startsWith('/api/api/currentUser') || path.startsWith('/api/logout') || path.startsWith('/api/login')
        || path.startsWith('/api/sanctum/csrf-cookie')
        || path.startsWith('/api/api/users')
        || path.endsWith('user/teams')
        || path.startsWith('/api/oauth/callback')
        || path.startsWith('/api/oauth/redirect')
        || path.startsWith('/api/api/agency')
        || path.startsWith('/api/api/avatars')
        || path.startsWith('/api/api/crm/myrequests/openAccess')
        || path.startsWith('/api/api/contactus')
        || path.startsWith('/api/api/uber/')
        || path.startsWith('/api/api/informUs')
        || path.startsWith('/api/api/omcs')
        || path.startsWith('/api/api/reset')
        || path.startsWith('/api/api/teams')
        || path.startsWith('/api/api/videos')
        || path.startsWith('/api/api/logError')
        || path.startsWith('/api/api/login')
        || path.startsWith('/api/api/requestDemoAccess')
        || path.startsWith('/api/api/teams')
        || path.startsWith('/api/api/agency')
        || path.startsWith('/xeroApi')
        || path.startsWith('/api/api/pageVisit')
        || path.startsWith('/api/api/stats')
    ) {
        return true;
    }
    return false;
}

function getTenantId(req: HttpRequest<unknown>) {
    const path = req.url.startsWith('http') ? new URL(req.url).pathname : req.url;
    const prefix = TenantService.bareAjaxPath;
    const afterPrefix = path.startsWith(prefix) ? path.substring(prefix.length) : '/shocker:tenantId/'+path;
    const tenantId = afterPrefix.split('/').filter(Boolean)[0] || '/shocker:tenantId/';
    return tenantId;
}

/**
 * Agent managed data - a request or action the agency works on for one of its OMCs - is written to
 * that OMC's tenant rather than the page's, so the agency's `agents` grant gets checked server side
 * (see AbstractAgentManagedHttpService). That is a legitimate mismatch, but only for a team the
 * user actually holds a grant in: currentRole.agentRoles is the client's copy of the same `agents`
 * rows switchTeam reads, so a tenant not in it is still refused.
 */
function agentGrantedTeam(tenantId: string, currentUserSvc: CurrentUserService) {
    const agentRoles = currentUserSvc.currentUser?.currentRole?.agentRoles;
    return !!agentRoles?.some(role => role.omcTeamId === tenantId);
}

function reject(message: string, req: HttpRequest<unknown>) {
    console.warn ({message, req});
    return throwError(
      new HttpErrorResponse({
        status: 977,
        statusText: 'Intercepted by TenantIdInterceptor',
        error: `${message} on URL ${req.url}`,
      })
    );    
}

export const tenantIdInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const tenantSvc = inject(TenantService);
  const currentUserSvc = inject(CurrentUserService);
  const expected = tenantSvc.getCurrentTenantId();
  const tenantId = getTenantId(req);

  const dblSlash = req.url.indexOf('//');
  if (dblSlash >= 0) {
    return reject(`Digital Ocean rejected //`, req);    
  } else if (tenantId === ':tenantId') {
    return reject(' : tenantId not allowed', req);
  } else if (tenantId !== expected && !urlWhitelisted(req.url)
             && !agentGrantedTeam(tenantId, currentUserSvc)) {
    console.error('wrong tenantId ', {tenantId, expected, url: req.url});
    return reject($localize `Invalid tenant id requested : [${tenantId}] expected [${expected}]`, req)
  }

  return next(req);
};

